import baseUrl from "../api/config";

// Utility function to get cookie value
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
}

// Full add to cart flow: add to cart, fetch minicart, fetch recommendations
// Following EXACT Angular master branch implementation patterns and payload structure
export async function addToCartFlow(
  product,
  countryCode = "in",
  languageCode = "en"
) {
  // Always get cartId from cookie (matching Angular master branch pattern)
  const cartId = getCookie("cartId") || "1129033042";
  const base = baseUrl || "";

  try {
    // Build payload following EXACT Angular master branch structure
    const lineItems = [
      {
        quantity: "1",
        lineNo: "",
        parentLineNo: "",
        price:
          product.price_data?.unFormattedPrice?.finalPrice ||
          product.price_data?.price ||
          "",
        product: {
          sku: product.sku || "",
          productSize: "",
          description: "",
          productAttributes: {
            id: product.productID || "",
            name: "",
            purity: "",
            scale: "",
            baseLineSku: "",
            ratePlanId: "",
            ratePlanName: "",
            ratePlanCharges: [
              {
                id: "",
                billingPeriod: "",
              },
            ],
          },
        },
        lotNumber: "",
        lotType: "",
      },
    ];

    const addToCartPayload = {
      cartId: cartId,
      cartName: "Cart",
      countryCode: countryCode,
      languageCode: languageCode,
      lineItems: lineItems,
    };

    // 1. Add to Cart API call (matching Angular master branch exactly)
    const addRes = await fetch(`${base}/api/store/carts/addToCart`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-Custom-header": true,
      },
      credentials: "include", // Ensure cookies are sent (matching Angular $http behavior)
      body: JSON.stringify(addToCartPayload),
    });
    if (!addRes.ok) throw new Error("Add to cart failed");
    await addRes.json(); // Parse response to ensure API call completed successfully

    // 2. Trigger minicart UI update (this triggers /store/v2/minicart internally)
    const miniCart = window.miniCart;
    if (miniCart) {
      miniCart.showCart(true);
      miniCart.updateItemCountInHeader();
    }

    // 3. Fetch Recommendations Products (matching Angular master branch pattern)
    const recRes = await fetch(`${base}/api/store/recommendations/products`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      credentials: "include",
      body: JSON.stringify({
        catalogNumber: [product.sku],
        countryCode,
        languageCode,
        app: "cart-overlay",
      }),
    });
    if (!recRes.ok) throw new Error("Fetch recommendations failed");
    const recData = await recRes.json();

    // 4. Trigger analytics event
    triggerAddToCartAnalytics(product);

    // Return data following genomic search UI pattern
    return { recData };
  } catch (error) {
    // Log error or handle as needed
    return { error: error.message };
  }
}

// Analytics event for add to cart (following genomic-product-search-ui pattern)
export function triggerAddToCartAnalytics(product) {
  if (document?.dispatchEvent && window?.CustomEvent) {
    document?.dispatchEvent?.(
      new window.CustomEvent("cartAdd", {
        detail: {
          products: {
            catalogNumber: product.sku || "",
            price:
              product.price_data?.unFormattedPrice?.finalPrice ||
              product.price_data?.price ||
              "",
            quantity: "1",
            designId: product.sku || "",
            uom: product.size || "",
            title: product.name || "",
            categoryName: product.brand || "",
            lineName: product.label || "",
            addMethod: "product-recommendations",
          },
        },
      })
    );
  }
}

// Bulk add to cart flow: add multiple products in a single API call
// Based on the curl payload structure provided
export async function addAllProductsToCart(
  products,
  countryCode = "in",
  languageCode = "en"
) {
  // Always get cartId from cookie (matching Angular master branch pattern)
  const cartId = getCookie("cartId") || "1129033042";
  const base = baseUrl || "";

  try {
    // Build lineItems array for all products following the curl payload structure
    const lineItems = products.map((product) => ({
      quantity: "1",
      lineNo: "",
      parentLineNo: "",
      price:
        product.price_data?.unFormattedPrice?.finalPrice ||
        product.price_data?.price ||
        "",
      product: {
        sku: product.sku || "",
        productSize: "",
        description: "",
        productAttributes: {
          id: product.productID || "",
          name: "",
          purity: "",
          scale: "",
          baseLineSku: "",
          ratePlanId: "",
          ratePlanName: "",
          ratePlanCharges: [
            {
              id: "",
              billingPeriod: "",
            },
          ],
        },
      },
      lotNumber: "",
      lotType: "",
    }));

    const addToCartPayload = {
      cartName: "Cart",
      countryCode: countryCode,
      languageCode: languageCode,
      lineItems: lineItems,
      cartId: cartId,
    };

    // 1. Add all products to Cart in single API call
    const addRes = await fetch(`${base}/api/store/carts/addToCart`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-Custom-header": true,
      },
      credentials: "include", // Ensure cookies are sent (matching Angular $http behavior)
      body: JSON.stringify(addToCartPayload),
    });
    if (!addRes.ok) throw new Error("Add all to cart failed");
    await addRes.json(); // Parse response to ensure API call completed successfully

    // 2. Trigger minicart UI update (this triggers /store/v2/minicart internally)
    const miniCart = window.miniCart;
    if (miniCart) {
      miniCart.showCart(true);
      miniCart.updateItemCountInHeader();
    }

    // 3. Trigger analytics event for bulk add (using first product as representative)
    if (products.length > 0) {
      triggerAddToCartAnalytics(products[0]);
    }

    // Return data following genomic search UI pattern
    return { productsCount: products.length };
  } catch (error) {
    // Log error or handle as needed
    return { error: error.message };
  }
}
